import React from "react";
import {
  Box,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import Input from "../../../../../components/Input";
import { useCateringStore } from "../../../../../store/useCateringStore";
import {
  OperationsDetailField,
  OperationsDetailGrid,
  OperationsSectionHeader,
} from "../../../ifas/components/OperationsUI";

export default function Logistics({
  isEditable = false,
}: {
  isEditable?: boolean;
}) {
  const { selectedCatering, setCateringLogistics, setCateringStaffing } =
    useCateringStore();
  const item = selectedCatering?.logistics as any;
  const staffing = selectedCatering?.staffing as any;

  const handleLogisticsChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setCateringLogistics({
        [field]: value,
      });
    };

  const handleStaffingChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      setCateringStaffing({
        [field]:
          field === "serversRequired" ||
          field === "supervisorsRequired" ||
          field === "driversRequired"
            ? value || ""
            : value,
      });
    };

  // Read-only view: always show the detail cards until user explicitly enters edit mode.
  if (!isEditable) {
    return (
      <Box>
        <OperationsSectionHeader title="Logistics" />
        <OperationsDetailGrid>
          <OperationsDetailField
            label="Indoor / Outdoor"
            value={item?.indoorOutdoor || "Indoor"}
          />
          <OperationsDetailField
            label="Setup Location"
            value={item?.setupLocation}
          />
          <OperationsDetailField
            label="Equipment Required"
            value={item?.accessNotes}
            span={3}
          />
        </OperationsDetailGrid>

        <Box mt={2.5}>
          <OperationsSectionHeader title="Staffing" />
          <OperationsDetailGrid>
            <OperationsDetailField
              label="Servers"
              value={staffing?.serversRequired}
            />
            <OperationsDetailField
              label="Supervisors"
              value={staffing?.supervisorsRequired}
            />
            <OperationsDetailField
              label="Drivers"
              value={staffing?.driversRequired}
            />
            <OperationsDetailField
              label="Arrival Time"
              value={staffing?.staffArrivalTime}
            />
          </OperationsDetailGrid>
        </Box>
      </Box>
    );
  }

  return (
    <Grid
      container
      rowSpacing={5}
      columnSpacing={10}
      mt={1}
      textAlign="left"
      p={3}
    >
      <Grid size={{ xs: 12, md: 4 }}>
        <Typography
          variant="body1"
          fontWeight={600}
          fontSize={18}
          sx={{ display: "block", textAlign: "left", mb: 1 }}
        >
          Indoor / Outdoor
        </Typography>
        <RadioGroup
          row
          value={item?.indoorOutdoor || "Indoor"}
          onChange={handleLogisticsChange("indoorOutdoor")}
        >
          <FormControlLabel value="Indoor" control={<Radio />} label="Indoor" />
          <FormControlLabel
            value="Outdoor"
            control={<Radio />}
            label="Outdoor"
          />
        </RadioGroup>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          value={item?.setupLocation || ""}
          onChange={handleLogisticsChange("setupLocation")}
          height={40}
          width="100%"
          label="Setup Location"
          backgroundColor="background.paper"
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected: unknown) => String(selected || ""),
            MenuProps: {
              PaperProps: {
                sx: {
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  maxHeight: "300px",
                },
              },
            },
          }}
        >
          <MenuItem value="Home">Home</MenuItem>
          <MenuItem value="Church">Church</MenuItem>
          <MenuItem value="hall">Hall</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </Input>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          value={staffing?.serversRequired ?? ""}
          onChange={handleStaffingChange("serversRequired")}
          type="number"
          height={40}
          width="100%"
          label="Servers"
          backgroundColor="background.paper"
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          value={staffing?.supervisorsRequired ?? ""}
          onChange={handleStaffingChange("supervisorsRequired")}
          type="number"
          height={40}
          width="100%"
          label="Supervisors"
          backgroundColor="background.paper"
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          value={staffing?.driversRequired ?? ""}
          onChange={handleStaffingChange("driversRequired")}
          type="number"
          height={40}
          width="100%"
          label="Drivers"
          backgroundColor="background.paper"
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled
          value={staffing?.staffArrivalTime || ""}
          type="time"
          height={40}
          width="100%"
          label="Arrival Time"
          backgroundColor="background.paper"
        />
      </Grid>

      <Grid size={{ xs: 12, md: 12 }}>
        <Input
          value={item?.accessNotes || ""}
          onChange={handleLogisticsChange("accessNotes")}
          height={100}
          width="100%"
          label="Equipment Required"
          placeholder="Enter equipment required"
          backgroundColor="background.paper"
          multiline
          inputProps={{ maxLength: 300 }}
          helperText={`${item?.accessNotes?.length || 0}/300`}
        />
      </Grid>
    </Grid>
  );
}
